import { useEffect, useRef, useState } from "react";
import type {
  HandLandmarker,
  NormalizedLandmark,
} from "@mediapipe/tasks-vision";

import { scanCopy } from "../config/copy";
import { PalmMaskOverlay } from "./camera-mask-overlay";
import { ScanNavbar } from "./scan-navbar";

/** Palm-line geometry over MediaPipe landmarks (start/end/control indices + style). */
const LINES = [
  {
    start: 5,
    end: 17,
    control: 13,
    color: "#FEFD00",
    curve: 0.5,
    sy: 0.03,
    ey: 0.05,
    sx: 0,
    ex: 0,
  },
  {
    start: 5,
    end: 13,
    control: 13,
    color: "#5BC2E7",
    curve: 0.2,
    sy: 0.04,
    ey: 0.1,
    sx: 0,
    ex: -0.02,
  },
  {
    start: 0,
    end: 5,
    control: 13,
    color: "#6CC24A",
    curve: 0.5,
    sy: -0.02,
    ey: 0.07,
    sx: 0,
    ex: 0,
  },
  {
    start: 0,
    end: 9,
    control: 9,
    color: "#EE2737",
    curve: 0.2,
    sy: -0.02,
    ey: 0.1,
    sx: 0.01,
    ex: 0.05,
  },
] as const;

/** CSS-pixel stroke width (canvas is backed at devicePixelRatio). */
const LINE_WIDTH = 2;
const SAMPLES = 40;
const LINE_DURATION_MS = 1000;
const DOT_DURATION_MS = 100;
const DOT_RADIUS = 3;
/** Global Y shift for lines only (not dots). Negative = higher on the palm. */
const LINES_OFFSET_Y = -0.01;

interface PalmPhotoResultProps {
  imageSrc: string
  landmarker: HandLandmarker | null
  ready: boolean
  /** Which palm was captured — right mirrors horizontal line offsets. Defaults to left. */
  hand?: "left" | "right"
  onContinue: () => void
  onRetake: () => void
  onBack: () => void
}

/** Captured photo with animated palm lines and Retake / Continue actions. */
export function PalmPhotoResult({
  imageSrc,
  landmarker,
  ready,
  hand = "left",
  onContinue,
  onRetake,
  onBack,
}: PalmPhotoResultProps) {
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);

  const [imgLoaded, setImgLoaded] = useState(false);
  const [handDetected, setHandDetected] = useState<boolean | null>(null);
  const [linesReady, setLinesReady] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  // Cached photos (warmDecode) can be complete before onLoad attaches — check both.
  // Reset detection state when the photo changes (retake).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset local UI state on retake
    setImgLoaded(false);
    setHandDetected(null);
    setLinesReady(false);
    setTimedOut(false);
    const image = imageRef.current;
    if (image?.complete && image.naturalWidth > 0) {
      setImgLoaded(true);
    }
  }, [imageSrc]);

  // Safety net: never dead-end this step. If the landmarks haven't drawn within a
  // few seconds — e.g. the model failed to initialize in a locked-down in-app
  // browser (Instagram) — unblock Continue so the funnel can't trap the user.
  // Re-armed on retake (imageSrc change).
  useEffect(() => {
    const timer = window.setTimeout(() => setTimedOut(true), 8000);
    return () => window.clearTimeout(timer);
  }, [imageSrc]);

  useEffect(() => {
    if (!ready || !imgLoaded || !imageSrc) return;
    const image = imageRef.current;
    const canvas = canvasRef.current;
    if (!landmarker || !image || !canvas) return;
    if (image.naturalWidth <= 0 || image.naturalHeight <= 0) return;

    let cancelled = false;
    const cancelRaf = () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    // Draw at on-screen resolution (× DPR) so strokes stay thin and crisp when the
    // photo is scaled by object-cover — not photo-pixel size (which upscales badly).
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const cssW = canvas.clientWidth || image.clientWidth || image.naturalWidth;
    const cssH =
      canvas.clientHeight || image.clientHeight || image.naturalHeight;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // The photo fills the box with object-cover (centered). Map normalized image
    // coords through the same cover-fit so lines/dots land on the palm.
    const iw = image.naturalWidth;
    const ih = image.naturalHeight;
    const coverScale = Math.max(cssW / iw, cssH / ih);
    const renderW = iw * coverScale;
    const renderH = ih * coverScale;
    const offsetX = (cssW - renderW) / 2;
    const offsetY = (cssH - renderH) / 2;
    const mapX = (nx: number) => offsetX + nx * renderW;
    const mapY = (ny: number) => offsetY + ny * renderH;

    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = LINE_WIDTH;

    type Pt = { x: number; y: number };

    const quad = (t: number, p0: number, p1: number, p2: number) => {
      const u = 1 - t;
      return u * u * p0 + 2 * u * t * p1 + t * t * p2;
    };

    const buildPoints = (
      landmarks: NormalizedLandmark[],
      line: (typeof LINES)[number],
    ): Pt[] => {
      // Right palm: mirror horizontal offsets tuned for the left hand.
      const mirrorX = hand === "right" ? -1 : 1
      const sx = line.sx * mirrorX
      const ex = line.ex * mirrorX
      const startX = mapX(landmarks[line.start].x) + renderW * sx
      const startY =
        mapY(landmarks[line.start].y) + renderH * (line.sy + LINES_OFFSET_Y)
      const endX = mapX(landmarks[line.end].x) + renderW * ex
      const endY =
        mapY(landmarks[line.end].y) + renderH * (line.ey + LINES_OFFSET_Y)
      const controlX =
        startX + (mapX(landmarks[line.control].x) - startX) * line.curve
      const controlY =
        startY +
        (mapY(landmarks[line.control].y) + renderH * LINES_OFFSET_Y - startY) *
          line.curve

      return Array.from({ length: SAMPLES }, (_, i) => {
        const t = i / (SAMPLES - 1)
        return {
          x: quad(t, startX, controlX, endX),
          y: quad(t, startY, controlY, endY),
        }
      })
    }

    const strokePath = (pts: Pt[], count: number, color: string) => {
      if (count < 2) return;
      ctx.strokeStyle = color;
      ctx.lineWidth = LINE_WIDTH;
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < count - 1; i++) {
        const xc = (pts[i].x + pts[i + 1].x) / 2;
        const yc = (pts[i].y + pts[i + 1].y) / 2;
        ctx.quadraticCurveTo(pts[i].x, pts[i].y, xc, yc);
      }
      ctx.lineTo(pts[count - 1].x, pts[count - 1].y);
      ctx.stroke();
    };

    const drawDotsStatic = (landmarks: NormalizedLandmark[]) => {
      for (const point of landmarks) {
        ctx.beginPath();
        ctx.arc(mapX(point.x), mapY(point.y), DOT_RADIUS, 0, Math.PI * 2);
        ctx.fillStyle = "#EE2737";
        ctx.fill();
      }
    };

    const drawDots = (
      landmarks: NormalizedLandmark[],
      onComplete: () => void,
    ) => {
      let current = 0;
      let startTime: number | null = null;

      const step = (ts: number) => {
        if (cancelled) return;
        if (startTime === null) startTime = ts;
        const progress = Math.min((ts - startTime) / DOT_DURATION_MS, 1);
        const point = landmarks[current];
        ctx.beginPath();
        ctx.arc(
          mapX(point.x),
          mapY(point.y),
          DOT_RADIUS * progress,
          0,
          Math.PI * 2,
        );
        ctx.fillStyle = "#EE2737";
        ctx.fill();

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(step);
          return;
        }
        startTime = null;
        current += 1;
        if (current < landmarks.length) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          onComplete();
        }
      };
      rafRef.current = requestAnimationFrame(step);
    };

    const drawLines = (landmarks: NormalizedLandmark[]) => {
      const paths = LINES.map((line) => ({
        pts: buildPoints(landmarks, line),
        color: line.color,
      }));
      let i = 0;

      const animateLine = () => {
        if (cancelled) return;
        if (i >= paths.length) {
          setLinesReady(true);
          return;
        }
        let startTime: number | null = null;

        const step = (ts: number) => {
          if (cancelled) return;
          if (startTime === null) startTime = ts;
          const progress = Math.min((ts - startTime) / LINE_DURATION_MS, 1);
          // Redraw from a clean slate each frame so strokes don't accumulate thickness.
          ctx.clearRect(0, 0, cssW, cssH);
          drawDotsStatic(landmarks);
          for (let k = 0; k < i; k++) {
            strokePath(paths[k].pts, SAMPLES, paths[k].color);
          }
          const count = Math.max(2, Math.round(progress * (SAMPLES - 1)) + 1);
          strokePath(paths[i].pts, count, paths[i].color);
          if (progress < 1) {
            rafRef.current = requestAnimationFrame(step);
          } else {
            i += 1;
            animateLine();
          }
        };
        rafRef.current = requestAnimationFrame(step);
      };
      animateLine();
    };

    const detect = () => {
      if (cancelled) return;
      try {
        const results = landmarker.detect(image);
        ctx.clearRect(0, 0, cssW, cssH);
        if (results.landmarks && results.landmarks.length > 0) {
          setHandDetected(true);
          drawDots(results.landmarks[0], () => drawLines(results.landmarks[0]));
        } else {
          setHandDetected(false);
        }
      } catch {
        setHandDetected(false);
      }
    };

    // Wait one frame so the img/canvas have laid out under object-cover before detect.
    rafRef.current = requestAnimationFrame(detect);

    return () => {
      cancelled = true;
      cancelRaf();
      ctx.clearRect(0, 0, cssW, cssH);
    };
  }, [ready, imgLoaded, imageSrc, landmarker, hand]);

  // The normal path enables Continue once the lines finish; the timeout is the
  // fallback for when the landmarker never became ready (handDetected still null).
  const canContinue =
    (handDetected === true && linesReady) ||
    (timedOut && handDetected === null);

  return (
    <div className="fixed inset-0 z-[1000] mx-auto w-full max-w-app bg-dark-base">
      <div className="absolute inset-0">
        <img
          ref={imageRef}
          src={imageSrc}
          alt=""
          onLoad={() => setImgLoaded(true)}
          className="absolute inset-0 size-full object-cover"
        />
        <canvas ref={canvasRef} className="absolute inset-0 size-full" />
        <PalmMaskOverlay variant="result" />
      </div>

      <div className="relative z-10 flex h-full flex-col">
        <ScanNavbar onBack={onBack} />

        <div className="flex-1" />

        <div className="flex flex-col gap-3 px-4 pb-4">
          {handDetected === false ? (
            <p className="text-center font-body text-sm text-peach">
              {scanCopy.result.noHand}
            </p>
          ) : null}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onRetake}
              className="flex h-14 flex-1 items-center justify-center rounded-full bg-purple-light/10 font-body text-base font-semibold text-purple-light"
            >
              {scanCopy.result.retake}
            </button>
            <button
              type="button"
              onClick={onContinue}
              disabled={!canContinue}
              className="flex h-14 flex-1 items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white disabled:opacity-50"
            >
              {scanCopy.result.continue}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
